<template>
  <div class="lensing-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$θ = 4GM / c²r$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>中心天体质量 M</label>
            <span class="val">{{ mass.toFixed(1) }} <small>⊙ (太阳质量)</small></span>
          </div>
          <div class="hud-item orange">
            <label>偏折角 Δθ</label>
            <span class="val">{{ (mass * 0.4).toFixed(2) }} <small>arcsec</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <radialGradient id="star-grad"><stop offset="0%" stop-color="#fff"/><stop offset="100%" stop-color="#3b82f6" opacity="0"/></radialGradient>
             <filter id="glow-lensing"><feGaussianBlur stdDeviation="6" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 背景星场 (遥远星系) -->
          <circle v-for="i in 20" :key="'s'+i" 
                  :cx="getDistortedStar(i).x" :cy="getDistortedStar(i).y" 
                  :r="getDistortedStar(i).r" fill="#ffffff" :opacity="getDistortedStar(i).opacity" />

          <!-- 中心大质量天体 (Black Hole / Massive Galaxy) -->
          <g transform="translate(225, 120)">
             <circle r="25" fill="#000" stroke="#334155" stroke-width="1" />
             <!-- 吸积盘模拟 -->
             <ellipse rx="60" ry="10" fill="none" stroke="#fbbf24" stroke-width="2" opacity="0.3" filter="url(#glow-lensing)" />
             <text y="40" text-anchor="middle" fill="#94a3b8" font-size="9">Lens Mass</text>
          </g>

          <!-- 光线偏折路径 (Light Paths) -->
          <g opacity="0.4">
             <path v-for="p in [1, -1]" :key="'p'+p" :d="getPath(p)" fill="none" stroke="#fbbf24" stroke-width="1.5" stroke-dasharray="2,2" />
          </g>

          <!-- 爱因斯坦环 (Einstein Ring) - 随质量变化 -->
          <circle cx="225" cy="120" :r="25 + mass * 4" fill="none" stroke="#fbbf24" stroke-width="1" opacity="0.1" stroke-dasharray="10,5" />
        </svg>

        <div class="physics-caption">
           现象：遥远星系发出的光在经过大质量天体附近时，受时空曲率影响产生偏折和重影。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节透镜天体质量 (Mass Level)：{{ mass.toFixed(1) }}</label>
                 <el-slider v-model="mass" :min="1" :max="20" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="info" size="mini" plain @click="mass = 10">重置为典型值</el-button>
              </div>
           </div>
        </div>
        
        <!-- 重点：高对比度“深渊黑/星光白”卡片，确保文字可见度 -->
        <div class="discovery-card-discovery-lensing" v-latex>
           <div class="icon-wrap"><i class="el-icon-view"></i></div>
           <div class="text-content">
             <p><strong>实战天文学：</strong>质量弯曲了时空，而光线只是在所谓“直”的时空中飞行。<strong>观察：</strong>调节质量，你会看到背景星点向外侧产生环形拉伸（畸变）。<strong>关键应用：</strong>引力透镜是天文学家的“宇宙放大镜”，它让我们能看见躲在暗处的大量**暗物质**，也验证了广义相对论的终极预言。</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'GravitationalLensingViz',
  data() {
    return {
      mass: 10
    }
  },
  methods: {
    getDistortedStar(i) {
       // Original position from a seed
       const seedX = (Math.sin(i * 12.3) * 0.5 + 0.5) * 450;
       const seedY = (Math.cos(i * 7.4) * 0.5 + 0.5) * 240;
       
       const dx = seedX - 225;
       const dy = seedY - 120;
       const dist = Math.hypot(dx, dy);
       
       // Lens warping factor
       const warp = (this.mass * 200) / (dist + 50);
       const nx = dx / dist;
       const ny = dy / dist;
       
       return {
          x: seedX + nx * warp,
          y: seedY + ny * warp,
          r: 1 + Math.random() * 2,
          opacity: 0.3 + (dist / 300)
       };
    },
    getPath(sign) {
       const curve = this.mass * 8;
       return `M 0,${120 + sign*80} Q 225,${120 + sign*(80-curve)} 450,${120 + sign*80}`;
    }
  }
}
</script>

<style scoped lang="scss">
.lensing-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #faf5ff; color: #6b21a8; border: 1px solid #e9d5ff; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-discovery-lensing { background: #faf5ff; padding: 12px 16px; border-radius: 10px; border: 1px solid #e9d5ff; display: flex; gap: 14px; align-items: center; i { color: #7e22ce; font-size: 1.4rem; } p { margin: 0; font-size: 11.5px; color: #581c87; line-height: 1.6; strong { color: #581c87; font-weight: 900; } } }
</style>
